import { User } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type SetStateAction } from "react";
import { sessionManager } from "../../broadcastChannel/sessionManager";
import {
  checkAuth,
  checkMailAndSendCode,
  register,
  signIn,
} from "../services/authService";
import { isPureFunder } from "../roleAccess";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface SharedAuthState extends AuthState {
  isAuthenticated: boolean;
}

const DEFAULT_AUTH_STATE: SharedAuthState = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
};

const hasWindow = typeof window !== "undefined";

const isPublicSubmitAccessPath = () => {
  if (!hasWindow) {
    return false;
  }

  const pathname = window.location.pathname;

  return (
    pathname.includes("/submit/user-partner-subscription/")
    || pathname.includes("/submit/user-subscription/")
    || pathname.includes("/submit/cfp-subscription/")
    || pathname.includes("/submit/authentification-cfp/")
    || pathname.includes("/authentification-cfp/")
  );
};

const getStoredToken = () => {
  if (!hasWindow) {
    return null;
  }

  return (
    localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken")
  );
};

const readStoredUser = (): User | null => {
  if (!hasWindow) {
    return null;
  }

  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser) as User;
  } catch {
    localStorage.removeItem("user");
    return null;
  }
};

const persistUser = (user: User | null) => {
  if (!hasWindow) {
    return;
  }

  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
    return;
  }

  localStorage.removeItem("user");
  sessionStorage.removeItem("user");
};

const clearAuthStorage = () => {
  if (!hasWindow) {
    return;
  }

  localStorage.removeItem("jwtToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");

  sessionStorage.removeItem("jwtToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user");
};

const buildAuthSnapshot = (state: AuthState): SharedAuthState => ({
  ...state,
  isAuthenticated: Boolean(state.user),
});

const getInitialAuthState = (): SharedAuthState => {
  const token = getStoredToken();
  const user = token ? readStoredUser() : null;

  if (!token) {
    return {
      ...DEFAULT_AUTH_STATE,
      loading: false,
    };
  }

  return {
    user,
    loading: true,
    error: null,
    isAuthenticated: Boolean(user),
  };
};

let sharedAuthState = getInitialAuthState();
let hasInitializedAuth = false;
let sharedLoadUserPromise: Promise<User | null> | null = null;
const authListeners = new Set<(state: SharedAuthState) => void>();

const notifyAuthListeners = () => {
  authListeners.forEach((listener) => listener(sharedAuthState));
};

const setSharedAuthState = (nextState: SetStateAction<AuthState>) => {
  const previousState: AuthState = {
    user: sharedAuthState.user,
    loading: sharedAuthState.loading,
    error: sharedAuthState.error,
  };

  const resolvedState =
    typeof nextState === "function"
      ? (nextState as (prevState: AuthState) => AuthState)(previousState)
      : nextState;

  persistUser(resolvedState.user);
  sharedAuthState = buildAuthSnapshot(resolvedState);
  notifyAuthListeners();
};

const refreshUser = async (forceUpdate = false) => {
  if (!forceUpdate && sharedLoadUserPromise) {
    return sharedLoadUserPromise;
  }

  sharedLoadUserPromise = checkAuth().finally(() => {
    sharedLoadUserPromise = null;
  });

  return sharedLoadUserPromise;
};

const loadSharedUser = async (forceUpdate = false) => {
  const jwtToken = getStoredToken();

  if (!jwtToken) {
    setSharedAuthState({ user: null, loading: false, error: null });
    return null;
  }

  const storedUser = readStoredUser();
  if (storedUser) {
    sharedAuthState = {
      user: storedUser,
      loading: true,
      error: null,
      isAuthenticated: true,
    };
    notifyAuthListeners();
  } else {
    setSharedAuthState((prevState) => ({
      ...prevState,
      loading: true,
      error: null,
    }));
  }

  try {
    const user = await refreshUser(forceUpdate);

    if (user?.status === 2) {
      clearAuthStorage();
      setSharedAuthState({ user: null, loading: false, error: null });
      if (!isPublicSubmitAccessPath()) {
        window.location.href = "/login";
      }
      return null;
    }

    setSharedAuthState({ user, loading: false, error: null });
    return user;
  } catch {
    setSharedAuthState({
      user: null,
      loading: false,
      error: "Failed to load user",
    });
    return null;
  }
};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<SharedAuthState>(sharedAuthState);

  useEffect(() => {
    const listener = (nextState: SharedAuthState) => {
      setAuthState(nextState);
    };

    authListeners.add(listener);

    if (!hasInitializedAuth) {
      hasInitializedAuth = true;
      void loadSharedUser();
    }

    return () => {
      authListeners.delete(listener);
    };
  }, []);

  const signInUser = async (
    email: string,
    password: string,
    rememberMe: boolean
  ) => {
    setSharedAuthState((prevState) => ({ ...prevState, loading: true }));

    try {
      const user = await signIn({ email, password, rememberMe });

      if (isPureFunder(user)) {
        sessionManager.logout();
        clearAuthStorage();
        queryClient.clear();
        queryClient.invalidateQueries();
        setSharedAuthState({ user: null, loading: false, error: null });
        return { success: false };
      }

      setSharedAuthState({ user, loading: false, error: null });
      void loadSharedUser(true);

      return { success: true };
    } catch {
      setSharedAuthState({
        user: null,
        loading: false,
        error: "Failed to sign in",
      });
      return { success: false };
    }
  };

  const registerUser = async (
    firstname: string,
    lastname: string,
    structureName: string,
    email: string,
    password: string,
    confirmPassword: string,
    siret: string,
    siren: string,
    numeroVoie: string,
    rue: string,
    codePostal: string,
    ville: string,
    pays: string,
    cacs: string[],
    cac1Sub?: string,
    isParticulier?: boolean
  ) => {
    setSharedAuthState((prevState) => ({ ...prevState, loading: true }));

    try {
      const { user, errors } = await register({
        firstname,
        lastname,
        structureName,
        email,
        password,
        confirmPassword,
        siret,
        siren,
        numeroVoie,
        rue,
        codePostal,
        ville,
        pays,
        cacs,
        cac1Sub,
        isParticulier,
      });

      if (errors) {
        setSharedAuthState({
          user: null,
          loading: false,
          error: "Failed to register",
        });
        return { success: false, errors };
      }

      setSharedAuthState({ user, loading: false, error: null });
      return {
        success: true,
        errors: null,
        existingUserUpdated: !!user?.existingUserUpdated,
        passwordInitialized: !!user?.passwordInitialized,
        message: user?.message ?? null,
      };
    } catch {
      setSharedAuthState({
        user: null,
        loading: false,
        error: "Failed to register",
      });
      return {
        success: false,
        errors: null,
        existingUserUpdated: false,
        passwordInitialized: false,
        message: null,
      };
    }
  };

  const logoutUser = useCallback(() => {
    clearAuthStorage();

    queryClient.clear();
    queryClient.invalidateQueries();

    setSharedAuthState({ user: null, loading: false, error: null });
    window.location.href = "/login";
  }, [queryClient]);

  const clearSession = useCallback(() => {
    clearAuthStorage();
    queryClient.clear();
    queryClient.invalidateQueries();
    setSharedAuthState({ user: null, loading: false, error: null });
  }, [queryClient]);

  const requestPasswordReset = async (mail: string) => {
    try {
      const { success } = await checkMailAndSendCode({ email: mail });
      return success;
    } catch {
      return { success: false };
    }
  };

  const updateUserState = (updatedUser: User) => {
    setSharedAuthState((prevState) => ({
      ...prevState,
      user: updatedUser,
    }));
  };

  return {
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    setAuthState: setSharedAuthState,
    signIn: signInUser,
    register: registerUser,
    logout: logoutUser,
    clearSession,
    requestPasswordReset,
    updateUserState,
    isAuthenticated: authState.isAuthenticated,
  };
};
